import React from "react";
import { Helmet } from "react-helmet-async";
import { resolveOgImage } from "../../utils/ogImage";
import { absoluteUrl } from "../../utils/siteUrl";
import { ldJsonString } from "../../utils/ldJson";

/**
 * Minimal per-route SEO — title, meta description, canonical URL and
 * Open Graph/Twitter card tags. No existing SEO infrastructure was found
 * anywhere in this app (see Phase 2 architecture notes), so this is the
 * smallest addition that covers the Guides routes without pulling in a
 * larger SEO framework. `path` is an app-relative path (e.g. the current
 * route); canonical / og:url are built from it on the fixed SITE_ORIGIN
 * (see utils/siteUrl.js), never window.location.origin, so the same page
 * can't claim two canonical URLs when reached via a non-canonical host.
 *
 * `jsonLd` (object or array of objects) is emitted as a single
 * <script type="application/ld+json"> — an @graph when given an array.
 * Pass schema.org nodes without their own @context; it's added here. Build
 * these with utils/structuredData.js (which prunes empty/undefined fields).
 *
 * `noindex` renders a `<meta name="robots" content="noindex, nofollow">`,
 * for private/member/utility pages (HomePage, AdminPage, Settings,
 * TicketVerification, the standalone /checkout route) that must stay
 * reachable but shouldn't appear in search results. Those pages call
 * `<Seo noindex />` alone, with no `path` — which is why `url` is only
 * computed when `path` is actually supplied: falling back to a bare origin
 * would emit an incorrect self-canonical on pages that should have none.
 *
 * `type` sets og:type, defaulting to "article" (every existing caller is
 * an article-like content page — Guides/Places). The homepage is the one
 * caller that passes `type="website"`, since DefaultSeo (rendered once at
 * the app root — see DefaultSeo.js) already sets og:type="website" and
 * react-helmet-async has the deeper-mounted <Helmet> win per tag, so
 * without this a page-level <Seo> would otherwise silently flip the
 * homepage's og:type to "article".
 *
 * `image` is the page's own social-preview image (a place's photo, a
 * guide's hero, an event's first image — always pass the largest existing
 * URL you have). It runs through resolveOgImage(), which keeps it only if
 * it's a usable absolute URL and otherwise substitutes the shared Small
 * Potatoes default — so og:image / twitter:image are always emitted, always
 * absolute, and this component owns its fallback instead of relying on
 * DefaultSeo's tags surviving underneath it.
 *
 * @component
 */
const Seo = ({ title, description, path, image, noindex, type = "article", jsonLd }) => {
  const url = path ? absoluteUrl(path) : undefined;
  const ogImage = resolveOgImage(image);

  // Same wrap + escape the server's crawler injection uses, so the two
  // <script> contents are byte-identical (see shared/seo/ldJson.js).
  const ldJsonText = ldJsonString(jsonLd);

  return (
    <Helmet>
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {url && <link rel="canonical" href={url} />}

      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {ldJsonText && <script type="application/ld+json">{ldJsonText}</script>}
    </Helmet>
  );
};

export default Seo;
